import { ref } from 'vue';
import { getModuleAccessRules } from '@/api/config';
import { useUserStore } from '@/store/modules/user';
import { getRouterAccessRules } from '@/router';

let moduleAccessRules = null;
let loading = false;

export const setAccessRules = (rules) => {
  moduleAccessRules = rules;
  console.log('[AccessControl] 规则已设置:', moduleAccessRules);
};

export const getAccessRules = () => {
  const routerRules = getRouterAccessRules();
  if (routerRules) {
    moduleAccessRules = routerRules;
    console.log('[AccessControl] 从路由获取规则:', moduleAccessRules);
  }
  return moduleAccessRules;
};

export const initAccessRules = async (forceRefresh = false) => {
  console.log('[AccessControl] initAccessRules called, forceRefresh:', forceRefresh, 'current rules:', moduleAccessRules);
  
  const routerRules = getRouterAccessRules();
  if (routerRules && !forceRefresh) {
    moduleAccessRules = routerRules;
    console.log('[AccessControl] 使用路由层面的规则:', moduleAccessRules);
    return moduleAccessRules;
  }
  
  if (moduleAccessRules !== null && !forceRefresh) {
    console.log('[AccessControl] 使用缓存的规则:', moduleAccessRules);
    return moduleAccessRules;
  }
  
  if (loading) {
    console.log('[AccessControl] 等待加载完成...');
    await new Promise(resolve => setTimeout(resolve, 100));
    return moduleAccessRules;
  }
  
  loading = true;
  console.log('[AccessControl] 开始API请求...');
  try {
    const response = await getModuleAccessRules();
    console.log('[AccessControl] API响应:', response);
    if (response.code === 200 && response.data) {
      moduleAccessRules = response.data;
      console.log('[AccessControl] 加载的规则:', moduleAccessRules);
      return moduleAccessRules;
    }
  } catch (error) {
    console.error('[AccessControl] 获取访问规则失败:', error);
  } finally {
    loading = false;
  }
  
  console.log('[AccessControl] 使用默认空规则');
  return { rules: [], default_require_login: false, default_min_role_level: 0 };
};

const checkPathMatch = (pattern, path) => {
  console.log('[AccessControl] checkPathMatch:', { pattern, path });
  
  if (pattern === path) return true;
  if (pattern === '/*') return true;
  
  if (pattern.endsWith('/*')) {
    const basePath = pattern.slice(0, -2);
    return path.startsWith(basePath);
  }
  
  const regexPattern = pattern
    .replace(/:[a-zA-Z_]+/g, '[^/]+')
    .replace(/\*/g, '.*');
  const regex = new RegExp('^' + regexPattern + '$');
  const result = regex.test(path);
  console.log('[AccessControl] 正则匹配:', { regexPattern, result });
  return result;
};

export const checkAccess = (module, path) => {
  if (!moduleAccessRules || !moduleAccessRules.rules) {
    const routerRules = getRouterAccessRules();
    if (routerRules) {
      moduleAccessRules = routerRules;
      console.log('[AccessControl] checkAccess 从路由获取规则:', moduleAccessRules);
    }
  }
  
  console.log('[AccessControl] checkAccess调用:', { module, path, rules: moduleAccessRules });
  
  if (!moduleAccessRules || !moduleAccessRules.rules) {
    console.log('[AccessControl] 无规则或规则为空');
    return { require_login: false, min_role_level: 0, canAccess: true };
  }
  
  const relativePath = path.startsWith(`/${module}`) 
    ? path.slice(module.length + 1) || '/' 
    : path;
  
  console.log('[AccessControl] 计算的relativePath:', relativePath);
  
  for (const rule of moduleAccessRules.rules) {
    console.log('[AccessControl] 检查规则:', rule);
    if (rule.module === module && checkPathMatch(rule.path, relativePath)) {
      const userStore = useUserStore();
      const isLoggedIn = userStore.isLoggedIn || localStorage.getItem('isLoggedIn');
      const userRoleLevel = userStore.user?.role_level || 0;
      
      const canAccess = !rule.require_login || isLoggedIn;
      const hasRole = userRoleLevel >= (rule.min_role_level || 0);
      
      console.log('[AccessControl] 匹配到规则:', { 
        rule, 
        isLoggedIn, 
        userRoleLevel, 
        canAccess, 
        hasRole 
      });
      
      return {
        require_login: rule.require_login,
        min_role_level: rule.min_role_level,
        canAccess: canAccess && hasRole
      };
    }
  }
  
  console.log('[AccessControl] 没有匹配的规则，返回默认值');
  return {
    require_login: moduleAccessRules.default_require_login || false,
    min_role_level: moduleAccessRules.default_min_role_level || 0,
    canAccess: true
  };
};

export const filterCategoriesByAccess = (categories, module) => {
  console.log('[AccessControl] filterCategoriesByAccess调用:', { module, categories });
  
  const userStore = useUserStore();
  const isLoggedIn = userStore.isLoggedIn || localStorage.getItem('isLoggedIn');
  const userRoleLevel = userStore.user?.role_level || 0;
  
  console.log('[AccessControl] 用户状态:', { isLoggedIn, userRoleLevel });
  
  const result = categories.filter(category => {
    let pathsToCheck = [];
    
    if (module === 'blog') {
      pathsToCheck = [
        `/${category.slug || category.id}`,
        `/category/${category.id}`
      ];
    } else {
      pathsToCheck = [
        `/${module}/category/${category.id}`,
        `/category/${category.id}`
      ];
    }
    
    let requireLogin = false;
    let minRoleLevel = 0;
    
    for (const path of pathsToCheck) {
      const access = checkAccess(module, path);
      if (access.require_login) {
        requireLogin = true;
      }
      if (access.min_role_level > minRoleLevel) {
        minRoleLevel = access.min_role_level;
      }
    }
    
    console.log('[AccessControl] 分类检查:', { 
      categoryId: category.id, 
      categoryName: category.name,
      categorySlug: category.slug,
      pathsToCheck,
      requireLogin,
      minRoleLevel
    });
    
    if (!requireLogin) return true;
    
    if (!isLoggedIn) return false;
    
    if (minRoleLevel > 0 && userRoleLevel < minRoleLevel) {
      return false;
    }
    
    return true;
  });
  
  console.log('[AccessControl] 过滤后的分类:', result);
  return result;
};

export const useAccessControl = () => {
  const rules = ref(null);
  const isLoading = ref(false);
  
  const loadRules = async () => {
    isLoading.value = true;
    rules.value = await initAccessRules();
    isLoading.value = false;
    return rules.value;
  };
  
  return {
    rules,
    isLoading,
    loadRules,
    checkAccess,
    filterCategoriesByAccess
  };
};
